import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { MilitarGrupo, GrupoTipo, Grupo } from '../types/grupos';

interface GrupoWithTipo extends Grupo {
  tipo: GrupoTipo;
}

interface UserGroupMembership {
  grupos: MilitarGrupo[];
  gruposPorTipo: Record<string, MilitarGrupo[]>;
  tiposDeGrupos: GrupoTipo[];
  isLoading: boolean;
  error: string | null;
}

export const useUserGroups = (): UserGroupMembership & { reloadGroups: (silent?: boolean) => Promise<void> } => {
  const { militar } = useAuth();
  const [grupos, setGrupos] = useState<MilitarGrupo[]>([]);
  const [tiposDeGrupos, setTiposDeGrupos] = useState<GrupoTipo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUserGroups = useCallback(async (silent: boolean = false) => {
    if (!militar?.id) {
      return;
    }

    try {
      // Apenas mostra loading se não for um reload silencioso
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      // Buscar grupos do usuário
      const gruposResponse = await api.get(`/militares-grupos/militar/${militar.id}`);
      const userGroups = Array.isArray(gruposResponse.data) ? gruposResponse.data : [];

      // Buscar todos os tipos de grupos
      const tiposResponse = await api.get('/grupos-tipos');
      const allTipos = Array.isArray(tiposResponse.data) ? tiposResponse.data : [];

      setGrupos(userGroups);
      setTiposDeGrupos(allTipos);
    } catch (err: any) {
      console.error('[useUserGroups] Erro ao carregar grupos do usuário:', err);
      setError('Erro ao carregar informações dos grupos');
      setGrupos([]);
      setTiposDeGrupos([]);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [militar?.id]);

  useEffect(() => {
    loadUserGroups(true); // Sempre silencioso para não interferir na UI
  }, [loadUserGroups]);

  // Agrupar grupos do usuário por tipo - memorizado para evitar recalcular em cada render
  const gruposPorTipo = useMemo(() => {
    const agrupados = grupos.reduce((acc, militarGrupo) => {
      const tipoId = militarGrupo.grupo?.id_tipo;
      if (tipoId) {
        if (!acc[tipoId]) {
          acc[tipoId] = [];
        }
        acc[tipoId].push(militarGrupo);
      }
      return acc;
    }, {} as Record<string, MilitarGrupo[]>);

    // Ordenar grupos dentro de cada tipo pela ordem
    Object.keys(agrupados).forEach(tipoId => {
      agrupados[tipoId] = agrupados[tipoId].sort((a, b) => {
        const ordemA = a.grupo?.ordem || 0;
        const ordemB = b.grupo?.ordem || 0;
        return ordemA - ordemB;
      });
    });

    return agrupados;
  }, [grupos]);

  // Ordenar tipos pela ordem - memorizado para evitar recalcular em cada render
  const tiposOrdenados = useMemo(() => {
    return tiposDeGrupos
      .filter(tipo => gruposPorTipo[tipo.id]) // Só mostrar tipos onde o usuário tem grupos
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  }, [tiposDeGrupos, gruposPorTipo]);

  return {
    grupos,
    gruposPorTipo,
    tiposDeGrupos: tiposOrdenados,
    isLoading,
    error,
    reloadGroups: loadUserGroups
  };
};

// Hook para verificar permissões específicas
export const useGroupPermissions = () => {
  const userGroupsData = useUserGroups();
  const { grupos } = userGroupsData;

  const isAdmin = (grupoId: string): boolean => {
    const membership = grupos.find(g => g.grupo.id === grupoId);
    return membership?.grupoCargo?.admin === true;
  };

  const isResponsavel = (grupoId: string): boolean => {
    const membership = grupos.find(g => g.grupo.id === grupoId);
    return membership?.grupoCargo?.responsavel === true;
  };

  const canManageMembers = (grupoId: string): boolean => {
    return isAdmin(grupoId) || isResponsavel(grupoId);
  };

  const canManageActions = (grupoId: string): boolean => {
    return isAdmin(grupoId) || isResponsavel(grupoId);
  };

  const isMember = (grupoId: string): boolean => {
    const isMember = grupos.some(g => {
      const isInGrupo = g.grupo.id === grupoId;
      const notRemoved = !g.removido_em;

      return isInGrupo && notRemoved;
    });

    return isMember;
  };

  return {
    isAdmin,
    isResponsavel,
    canManageMembers,
    canManageActions,
    isMember,
    // Retornar também os dados dos grupos para sincronização
    ...userGroupsData
  };
};
